import { Discount } from '../../types/DiscountTypes'
import { getBasePriceFromCalculation, getPriceFromCalculation } from './CalculateDiscountHelpers'
import {
  CalculateDiscountLogic,
  Calculation,
  CalculationDiscount,
  CalculationUserInput,
  DiscountType
} from './CalculateDiscountTypes'

export class DiscountCouponCodeLogic implements CalculateDiscountLogic {
  type: DiscountType = 'code'
  discountDefinition: Discount

  constructor(discount: Discount) {
    this.discountDefinition = discount
  }

  chooseEligible(previousCalculations: Calculation[], userInput: CalculationUserInput) {
    const discountCode = userInput.discountCode
    if (!discountCode || !discountCode.isActive) return []

    const allowed = new Set(discountCode.allowedActivityIds ?? [])
    const excluded = new Set(discountCode.excludedActivityIds ?? [])
    const eligibleCalculations = previousCalculations.filter((c) => {
      if (excluded.has(c.activity.id)) return false
      if (allowed.size === 0) return true
      return (
        allowed.has(c.activity.id) || (c.activity.subeventParentId ? allowed.has(c.activity.subeventParentId) : false)
      )
    })

    if (eligibleCalculations.length === 0) {
      return []
    }

    // Default scope change (requested): for amount-based codes, missing scope => Enrollment
    const calculationType = discountCode.calculationType || 'amount'
    const scope = discountCode.discountScope ?? 'Enrollment'
    if (calculationType === 'amount' && scope === 'Enrollment') {
      const best = eligibleCalculations.reduce<Calculation | null>((best, current) => {
        if (!best) return current
        const bestPrice = getPriceFromCalculation(best)
        const currentPrice = getPriceFromCalculation(current)
        return currentPrice > bestPrice ? current : best
      }, null)
      return best ? [best.activity.id] : []
    }

    // Per activity (default for percentage codes, or explicitly set)
    return eligibleCalculations.map((calculation) => calculation.activity.id)
  }

  calculate(previousCalculations: Calculation[], userInput: CalculationUserInput) {
    const alreadyApplied = previousCalculations.some((calculation) =>
      (calculation.discounts || []).some((discount) => discount.type === 'code')
    )
    if (alreadyApplied) return previousCalculations

    const eligibleActivities = this.chooseEligible(previousCalculations, userInput)
    if (eligibleActivities.length === 0) return previousCalculations

    const discountCode = userInput.discountCode
    const calculationType = discountCode?.calculationType || 'amount'
    const amountValueRaw = discountCode?.discountAmount
    const amountValue =
      calculationType === 'amount' && (!amountValueRaw || amountValueRaw <= 0)
        ? (discountCode?.discountValue ?? 0)
        : (amountValueRaw ?? discountCode?.discountValue ?? 0)
    const percentageValue = Math.min(Math.max(discountCode?.discountValue ?? 0, 0), 100)
    const scope = discountCode?.discountScope ?? 'Enrollment'
    const isEnrollmentAmount = calculationType === 'amount' && scope === 'Enrollment'
    let remainingEnrollmentAmount = amountValue

    // apply general discount amount to final price
    previousCalculations.map((calculation) => {
      if (!eligibleActivities.includes(calculation.activity.id)) return
      if (isEnrollmentAmount && remainingEnrollmentAmount <= 0) return
      const activityPrice = getPriceFromCalculation(calculation)
      const basePrice = getBasePriceFromCalculation(calculation)

      const override =
        discountCode?.activityOverrides?.[calculation.activity.id] ??
        (calculation.activity.subeventParentId
          ? discountCode?.activityOverrides?.[calculation.activity.subeventParentId]
          : undefined)
      const effectiveAmountValue = override?.discountAmount ?? amountValue
      const effectivePercentageValue = Math.min(Math.max(override?.discountValue ?? percentageValue, 0), 100)

      let discountAmount = 0
      if (calculationType === 'percentage') {
        discountAmount = Math.round((basePrice * effectivePercentageValue) / 100)
      } else {
        const cappedAmount = isEnrollmentAmount
          ? Math.min(effectiveAmountValue, remainingEnrollmentAmount)
          : effectiveAmountValue
        discountAmount = Math.min(cappedAmount, activityPrice) // cannot exceed activity price
        if (isEnrollmentAmount) remainingEnrollmentAmount -= discountAmount
      }

      const discount: CalculationDiscount = {
        type: this.type,
        name: this.discountDefinition.name,
        id: this.discountDefinition.id,
        input: activityPrice,
        discount: discountAmount,
        output: Math.max(0, activityPrice - discountAmount), // cannot go below zero
        discountCalculationType: calculationType,
        discountValue: calculationType === 'percentage' ? effectivePercentageValue : discountAmount,
        discountGroup: this.discountDefinition.discountGroup
      }
      calculation.discounts = calculation.discounts || []
      calculation.discounts.push(discount)
      calculation.finalPrice = discount.output
    })

    return previousCalculations
  }
}
